<template>
  <div id="subscription-payment-page">
    <v-overlay :value="ProgressLoadingFlag">
      <v-progress-circular indeterminate size="64"></v-progress-circular>
    </v-overlay>

    <!-- Premium Header Card -->
    <v-container class="px-4 py-6">
      <v-card
        elevation="24"
        class="premium-header-card mb-6 overflow-hidden"
        style="min-height: 140px"
      >
        <div class="header-gradient-bg">
          <div class="header-backdrop-effect"></div>
          <div class="header-content-premium pa-8">
            <v-row align="center" class="fill-height">
              <v-col cols="12" sm="8" class="d-flex align-center">
                <div class="header-icon-box">
                  <v-icon color="white" size="40">mdi-file-document</v-icon>
                </div>
                <div class="ml-6">
                  <h1 class="header-title white--text">{{ PageTitle }}</h1>
                  <p class="header-subtitle white--text">
                    {{ PageDescription }}
                  </p>
                </div>
              </v-col>
              <v-col cols="12" sm="4" class="text-right">
                <v-btn
                  small
                  @click.prevent="refreshPageData"
                  color="rgba(255,255,255,0.25)"
                  class="premium-action-btn white--text mr-2"
                >
                  <v-icon small>mdi-refresh</v-icon>&nbsp;Refresh
                </v-btn>
                <v-btn
                  small
                  @click.prevent="backPage"
                  color="rgba(255,255,255,0.25)"
                  class="premium-action-btn white--text"
                >
                  <v-icon small>mdi-arrow-left</v-icon>&nbsp;Back
                </v-btn>
              </v-col>
            </v-row>
          </div>
        </div>
      </v-card>
    </v-container>

    <v-container class="py-0 px-4">
      <v-card class="main-card">
        <v-card-text class="content-section">
          <v-form
            ref="form2"
            v-model="valid"
            lazy-validation
            v-on:submit.prevent="confirmAlert"
          >
            <div v-if="StartupLoadingFlag" class="text-center py-8">
              <v-progress-circular
                indeterminate
                size="50"
              ></v-progress-circular>
            </div>

            <template v-if="!StartupLoadingFlag">
              <!-- Member Details Card - Premium -->
              <v-card elevation="8" class="sponsor-card premium-shadow mb-6">
                <div class="card-header-premium">
                  <v-icon color="white" class="header-icon-large"
                    >mdi-account-multiple</v-icon
                  >
                  <h3 class="card-title-premium">Member Details</h3>
                </div>
                <v-container class="card-content-wrapper">
                  <v-row wrap class="align-center">
                    <!-- Member Profile -->
                    <v-col cols="12" sm="6" md="3">
                      <div class="profile-card">
                        <div class="profile-image-wrapper">
                          <v-img
                            :src="Member.MemberImagePath"
                            :alt="Member.MemberName"
                            lazy-src="/no-image-available.png"
                            aspect-ratio="1"
                            class="profile-image"
                          >
                            <template v-slot:placeholder>
                              <v-row
                                class="fill-height ma-0"
                                align="center"
                                justify="center"
                              >
                                <v-progress-circular
                                  indeterminate
                                  color="grey lighten-5"
                                ></v-progress-circular>
                              </v-row>
                            </template>
                          </v-img>
                        </div>
                        <div class="profile-info">
                          <h4 class="profile-name">{{ Member.MemberName }}</h4>
                          <p class="profile-id">{{ Member.MembershipId }}</p>
                        </div>
                      </div>
                    </v-col>

                    <!-- Personal Info Section -->
                    <v-col cols="12" sm="6" md="3">
                      <div class="info-card">
                        <div class="info-header">
                          <v-icon color="#1976d2" class="info-icon"
                            >mdi-information</v-icon
                          >
                          <span class="info-label">Personal Info</span>
                        </div>
                        <div class="info-content">
                          <div class="info-row-item">
                            <span class="info-label-small">Gender:</span>
                            <span class="info-value-small">{{
                              Member.GenderTxt
                            }}</span>
                          </div>
                          <div class="info-row-item">
                            <span class="info-label-small">DOB:</span>
                            <span class="info-value-small">{{
                              formatDate(Member.DOB)
                            }}</span>
                          </div>
                          <div class="info-row-item">
                            <span class="info-label-small">Status:</span>
                            <v-chip
                              small
                              :color="
                                Member.UserActiveStatus == 1
                                  ? 'success'
                                  : 'error'
                              "
                              text-color="white"
                            >
                              {{ Member.UserActiveStatusTxt }}
                            </v-chip>
                          </div>
                        </div>
                      </div>
                    </v-col>

                    <!-- Organization Info Section -->
                    <v-col cols="12" sm="6" md="3">
                      <div class="info-card">
                        <div class="info-header">
                          <v-icon color="#10b981" class="info-icon"
                            >mdi-building</v-icon
                          >
                          <span class="info-label">Organization</span>
                        </div>
                        <div class="info-content">
                          <div class="info-row-item">
                            <span class="info-label-small">LO:</span>
                            <span class="info-value-small font-weight-bold">{{
                              Member.LomName
                            }}</span>
                          </div>
                          <div class="info-row-item">
                            <span class="info-label-small">Zone:</span>
                            <span class="info-value-small">{{
                              Member.ZoneName
                            }}</span>
                          </div>
                          <div class="info-row-item" v-if="Member.Designation">
                            <span class="info-label-small">Designation:</span>
                            <v-chip
                              small
                              color="primary"
                              text-color="white"
                              class="mt-1"
                            >
                              {{ Member.Designation }}
                            </v-chip>
                          </div>
                        </div>
                      </div>
                    </v-col>

                    <!-- Contact Info Section -->
                    <v-col cols="12" sm="6" md="3">
                      <div class="info-card">
                        <div class="info-header">
                          <v-icon color="#e53935" class="info-icon"
                            >mdi-phone</v-icon
                          >
                          <span class="info-label">Contact</span>
                        </div>
                        <div class="info-content">
                          <div class="info-row-item">
                            <span class="info-label-small">Mobile:</span>
                            <span class="info-value-small">{{
                              Member.PrimaryMobile
                            }}</span>
                          </div>
                          <div class="info-row-item">
                            <span class="info-label-small">Email:</span>
                            <span class="info-value-small">{{
                              Member.EmailId
                            }}</span>
                          </div>
                        </div>
                      </div>
                    </v-col>
                  </v-row>
                </v-container>
              </v-card>

              <br /><br />

              <!-- NEW LO DETAILS CARD -->
              <v-card elevation="8" class="newlo-card premium-shadow mb-6">
                <div
                  class="card-header-premium"
                  style="
                    background: linear-gradient(
                      135deg,
                      #667eea 0%,
                      #764ba2 100%
                    );
                  "
                >
                  <v-icon color="white" class="header-icon-large"
                    >mdi-city</v-icon
                  >
                  <h3 class="card-title-premium">New LO Details</h3>
                </div>
                <v-container class="card-content-wrapper">
                  <v-row wrap>
                    <!-- Choose LO Name -->
                    <v-col cols="12" sm="6" md="4">
                      <div class="form-group">
                        <label class="form-label">
                          <span class="required-mark">*</span>Choose from
                          approved LO name
                        </label>
                        <v-autocomplete
                          :reduce="(option) => option.value"
                          :loading="NewLomCodeOptionsLoading"
                          :items="NewLomCodeOptions"
                          :rules="NewLomCodeRules"
                          v-model="NewLomCode"
                          required
                          outlined
                          dense
                          class="premium-input"
                          prepend-inner-icon="mdi-city-variant"
                          background-color="white"
                        ></v-autocomplete>
                      </div>
                    </v-col>

                    <!-- Selected LOM Name -->
                    <v-col cols="12" sm="6" md="2">
                      <div class="info-card">
                        <div class="info-header">
                          <v-icon color="#667eea" class="info-icon"
                            >mdi-text-box</v-icon
                          >
                          <span class="info-label">Selected LO</span>
                        </div>
                        <div class="info-content">
                          <h5 class="info-value">
                            {{ SelectedLom.NewLomsName }}
                          </h5>
                          <p class="info-subtext">
                            {{ SelectedLom.StateName }}
                          </p>
                        </div>
                      </div>
                    </v-col>

                    <!-- Paid Date -->
                    <v-col cols="12" sm="6" md="2">
                      <div class="info-card">
                        <div class="info-header">
                          <v-icon color="#10b981" class="info-icon"
                            >mdi-calendar-check</v-icon
                          >
                          <span class="info-label">Paid Date</span>
                        </div>
                        <div class="info-content">
                          <h5 class="info-value">
                            {{ SelectedLom.OrdersDateTxt }}
                          </h5>
                        </div>
                      </div>
                    </v-col>

                    <!-- Expiry Date -->
                    <v-col cols="12" sm="6" md="2">
                      <div class="info-card">
                        <div class="info-header">
                          <v-icon color="#e53935" class="info-icon"
                            >mdi-calendar-clock</v-icon
                          >
                          <span class="info-label">Expiry Date</span>
                        </div>
                        <div class="info-content">
                          <h5 class="info-value">
                            {{ SelectedLom.ExpiryDateTxt }}
                          </h5>
                        </div>
                      </div>
                    </v-col>

                    <!-- Is This A Lady LO -->
                    <v-col cols="12" sm="6" md="4">
                      <div class="form-group">
                        <label class="form-label">
                          <span class="required-mark">*</span>This A Lady LO?
                        </label>
                        <v-radio-group
                          v-model="IsLadyLo"
                          row
                          color="primary"
                          :rules="IsLadyLoRules"
                        >
                          <v-radio label="Yes" :value="true"></v-radio>
                          <v-radio label="No" :value="false"></v-radio>
                        </v-radio-group>
                      </div>
                    </v-col>

                    <!-- File Uploads -->
                    <v-col cols="12" sm="6" md="6">
                      <div class="form-group">
                        <label class="form-label">
                          <span class="required-mark">*</span>Orientation
                          Certificate (PDF)
                        </label>
                        <div class="file-upload-wrapper">
                          <v-file-input
                            :clearable="false"
                            truncate-length="25"
                            accept="application/pdf"
                            placeholder="Click to upload PDF"
                            v-model="OrientationCertificate"
                            prepend-icon="mdi-file-pdf-box"
                            class="file-input-premium"
                            show-size
                            background-color="white"
                          >
                            <template v-slot:append-outer>
                              <v-icon
                                v-if="
                                  OrientationCertificate &&
                                  Object.keys(OrientationCertificate).length > 0
                                "
                                color="#10b981"
                                @click="OrientationCertificate = {}"
                                class="delete-btn"
                              >
                                mdi-close-circle
                              </v-icon>
                            </template>
                          </v-file-input>
                        </div>
                      </div>
                    </v-col>

                    <v-col cols="12" sm="6" md="6">
                      <div class="form-group">
                        <label class="form-label">
                          <span class="required-mark">*</span>Signed Membership
                          List (PDF)
                        </label>
                        <div class="file-upload-wrapper">
                          <v-file-input
                            :clearable="false"
                            truncate-length="25"
                            accept="application/pdf"
                            placeholder="Click to upload PDF"
                            v-model="MembershipList"
                            prepend-icon="mdi-file-pdf-box"
                            class="file-input-premium"
                            show-size
                            background-color="white"
                          >
                            <template v-slot:append-outer>
                              <v-icon
                                v-if="
                                  MembershipList &&
                                  Object.keys(MembershipList).length > 0
                                "
                                color="#10b981"
                                @click="MembershipList = {}"
                                class="delete-btn"
                              >
                                mdi-close-circle
                              </v-icon>
                            </template>
                          </v-file-input>
                        </div>
                      </div>
                    </v-col>
                  </v-row>
                </v-container>
              </v-card>

              <!-- LADY LO WARNING ALERT - PREMIUM STYLE -->
              <v-card
                v-if="IsLadyLo"
                elevation="8"
                class="premium-alert-card mb-6 mt-4"
                style="
                  background: linear-gradient(
                    135deg,
                    rgba(252, 211, 77, 0.15) 0%,
                    rgba(249, 115, 22, 0.1) 100%
                  );
                  border-left: 5px solid #f59e0b;
                  border-radius: 12px;
                "
              >
                <v-container class="pa-6">
                  <v-row align="center" class="ma-0">
                    <v-col cols="auto" class="pr-4">
                      <div class="premium-alert-icon">
                        <v-icon
                          large
                          style="
                            color: #f59e0b;
                            animation: pulse-icon 2s infinite;
                          "
                        >
                          mdi-information-outline
                        </v-icon>
                      </div>
                    </v-col>
                    <v-col cols="auto" class="flex-grow-1">
                      <div class="premium-alert-content">
                        <h4
                          class="premium-alert-title"
                          style="
                            color: #d97706;
                            font-weight: 700;
                            font-size: 18px;
                            margin-bottom: 8px;
                          "
                        >
                          👩‍💼 Important Note - Lady LO
                        </h4>
                        <p
                          style="
                            color: #92400e;
                            font-size: 14px;
                            margin: 0;
                            line-height: 1.6;
                          "
                        >
                          Since this is a
                          <strong class="premium-highlight"
                            >Lady Local Organization</strong
                          >, all members of this LO must be
                          <strong class="premium-highlight"
                            >women/ladies only</strong
                          >. Please ensure that membership is restricted to
                          female members only to maintain the integrity and
                          purpose of this organization.
                        </p>
                      </div>
                    </v-col>
                  </v-row>
                </v-container>
              </v-card>

              <br /><br />

              <!-- NEW LO PRESIDENT DETAILS CARD -->
              <v-card elevation="8" class="president-card premium-shadow mb-6">
                <div
                  class="card-header-premium"
                  style="
                    background: linear-gradient(
                      135deg,
                      #f093fb 0%,
                      #f5576c 100%
                    );
                  "
                >
                  <v-icon color="white" class="header-icon-large"
                    >mdi-account-tie</v-icon
                  >
                  <h3 class="card-title-premium">New LO President Details</h3>
                </div>
                <v-container class="card-content-wrapper">
                  <v-row wrap>
                    <!-- Name Fields -->
                    <v-col cols="12" sm="6" md="4">
                      <div class="form-group">
                        <label class="form-label">
                          <span class="required-mark">*</span>First Name
                        </label>
                        <v-text-field
                          v-model="FirstName"
                          :rules="FirstNameRules"
                          hint="Enter first name"
                          required
                          outlined
                          dense
                          class="premium-input"
                          prepend-inner-icon="mdi-format-text"
                          background-color="white"
                        ></v-text-field>
                      </div>
                    </v-col>
                    <v-col cols="12" sm="6" md="4">
                      <div class="form-group">
                        <label class="form-label">Middle Name</label>
                        <v-text-field
                          v-model="MiddleName"
                          :rules="MiddleNameRules"
                          hint="Enter middle name"
                          required
                          outlined
                          dense
                          class="premium-input"
                          prepend-inner-icon="mdi-format-text"
                          background-color="white"
                        ></v-text-field>
                      </div>
                    </v-col>
                    <v-col cols="12" sm="6" md="4">
                      <div class="form-group">
                        <label class="form-label">Last Name</label>
                        <v-text-field
                          v-model="LastName"
                          :rules="LastNameRules"
                          hint="Enter last name"
                          required
                          outlined
                          dense
                          class="premium-input"
                          prepend-inner-icon="mdi-format-text"
                          background-color="white"
                        ></v-text-field>
                      </div>
                    </v-col>

                    <!-- Address Fields -->
                    <v-col cols="12">
                      <div class="form-group">
                        <label class="form-label">
                          <span class="required-mark">*</span>Street Address 1
                        </label>
                        <v-text-field
                          v-model="Street1"
                          :rules="Street1Rules"
                          hint="Enter street address line 1"
                          required
                          outlined
                          dense
                          class="premium-input"
                          prepend-inner-icon="mdi-home-variant"
                          background-color="white"
                        ></v-text-field>
                      </div>
                    </v-col>
                    <v-col cols="12" sm="8">
                      <div class="form-group">
                        <label class="form-label">Street Address 2</label>
                        <v-text-field
                          v-model="Street2"
                          :rules="Street2Rules"
                          hint="Enter street address line 2"
                          required
                          outlined
                          dense
                          class="premium-input"
                          prepend-inner-icon="mdi-home-variant"
                          background-color="white"
                        ></v-text-field>
                      </div>
                    </v-col>
                    <v-col cols="12" sm="4">
                      <div class="form-group">
                        <label class="form-label">Area</label>
                        <v-text-field
                          v-model="AreaName"
                          :rules="AreaNameRules"
                          hint="Enter area name"
                          required
                          outlined
                          dense
                          class="premium-input"
                          prepend-inner-icon="mdi-map-marker"
                          background-color="white"
                        ></v-text-field>
                      </div>
                    </v-col>

                    <!-- Location Fields -->
                    <v-col cols="12" sm="6" md="4">
                      <div class="form-group">
                        <label class="form-label">
                          <span class="required-mark">*</span>State
                        </label>
                        <v-autocomplete
                          @change="getDistrictCodeOptions"
                          :reduce="(option) => option.value"
                          :loading="StateCodeOptionsLoading"
                          :items="StateCodeOptions"
                          :rules="StateCodeRules"
                          v-model="StateCode"
                          required
                          outlined
                          dense
                          class="premium-input"
                          prepend-inner-icon="mdi-state"
                          background-color="white"
                        ></v-autocomplete>
                      </div>
                    </v-col>
                    <v-col cols="12" sm="6" md="4">
                      <div class="form-group">
                        <label class="form-label">
                          <span class="required-mark">*</span>District
                        </label>
                        <v-autocomplete
                          @change="getCityCodeOptions"
                          :reduce="(option) => option.value"
                          :loading="DistrictCodeOptionsLoading"
                          :items="DistrictCodeOptions"
                          :rules="DistrictCodeRules"
                          v-model="DistrictCode"
                          required
                          outlined
                          dense
                          class="premium-input"
                          prepend-inner-icon="mdi-city-variant"
                          background-color="white"
                        ></v-autocomplete>
                      </div>
                    </v-col>
                    <v-col cols="12" sm="6" md="4">
                      <div class="form-group">
                        <label class="form-label">
                          <span class="required-mark">*</span>City
                        </label>
                        <v-autocomplete
                          @change="generateSelectedLoName"
                          :reduce="(option) => option.value"
                          :loading="CityCodeOptionsLoading"
                          :items="CityCodeOptions"
                          :rules="CityCodeRules"
                          v-model="CityCode"
                          dense
                          clearable
                          required
                          outlined
                          class="premium-input"
                          prepend-inner-icon="mdi-map-marker"
                          background-color="white"
                        ></v-autocomplete>
                      </div>
                    </v-col>
                    <v-col cols="12" sm="6" md="4">
                      <div class="form-group">
                        <label class="form-label">
                          <span class="required-mark">*</span>Pincode
                        </label>
                        <v-text-field
                          v-model="Pincode"
                          :rules="PincodeRules"
                          hint="Enter pincode"
                          required
                          outlined
                          dense
                          class="premium-input"
                          prepend-inner-icon="mdi-mail-box"
                          background-color="white"
                        ></v-text-field>
                      </div>
                    </v-col>
                    <v-col cols="12" sm="6" md="4">
                      <div class="form-group">
                        <label class="form-label">
                          <span class="required-mark">*</span>Mobile No
                        </label>
                        <v-text-field
                          v-model="PresidentMobileNo"
                          :rules="PresidentMobileNoRules"
                          :counter="10"
                          v-mask="'##########'"
                          type="number"
                          hint="Enter mobile number"
                          dense
                          outlined
                          class="premium-input"
                          prepend-inner-icon="mdi-phone"
                          background-color="white"
                        ></v-text-field>
                      </div>
                    </v-col>
                    <v-col cols="12" sm="6" md="4">
                      <div class="form-group">
                        <label class="form-label">
                          <span class="required-mark">*</span>Email ID
                        </label>
                        <v-text-field
                          v-model="PresidentEmailId"
                          :rules="PresidentEmailIdRules"
                          hint="Enter email id"
                          type="email"
                          required
                          outlined
                          dense
                          class="premium-input"
                          prepend-inner-icon="mdi-email"
                          background-color="white"
                        ></v-text-field>
                      </div>
                    </v-col>
                  </v-row>
                </v-container>
              </v-card>
              <!-- NEW SECRETARY DETAILS CARD -->
              <v-card elevation="8" class="premium-shadow mb-6">
                <div
                  class="card-header-premium"
                  style="
                    background: linear-gradient(
                      135deg,
                      #fa709a 0%,
                      #fee140 100%
                    );
                  "
                >
                  <v-icon color="white" class="header-icon-large"
                    >mdi-account-check</v-icon
                  >
                  <h3 class="card-title-premium">New Secretary Details</h3>
                </div>
                <v-container class="card-content-wrapper">
                  <v-row wrap>
                    <!-- Name Fields -->
                    <v-col cols="12" sm="6" md="4">
                      <div class="form-group">
                        <label class="form-label">
                          <span class="required-mark">*</span>First Name
                        </label>
                        <v-text-field
                          v-model="SecretaryFirstName"
                          :rules="SecretaryFirstNameRules"
                          hint="Enter first name"
                          required
                          outlined
                          dense
                          class="premium-input"
                          prepend-inner-icon="mdi-format-text"
                          background-color="white"
                        ></v-text-field>
                      </div>
                    </v-col>
                    <v-col cols="12" sm="6" md="4">
                      <div class="form-group">
                        <label class="form-label">Middle Name</label>
                        <v-text-field
                          v-model="SecretaryMiddleName"
                          :rules="SecretaryMiddleNameRules"
                          hint="Enter middle name"
                          required
                          outlined
                          dense
                          class="premium-input"
                          prepend-inner-icon="mdi-format-text"
                          background-color="white"
                        ></v-text-field>
                      </div>
                    </v-col>
                    <v-col cols="12" sm="6" md="4">
                      <div class="form-group">
                        <label class="form-label">Last Name</label>
                        <v-text-field
                          v-model="SecretaryLastName"
                          :rules="SecretaryLastNameRules"
                          hint="Enter last name"
                          required
                          outlined
                          dense
                          class="premium-input"
                          prepend-inner-icon="mdi-format-text"
                          background-color="white"
                        ></v-text-field>
                      </div>
                    </v-col>

                    <!-- Address Fields -->
                    <v-col cols="12">
                      <div class="form-group">
                        <label class="form-label">
                          <span class="required-mark">*</span>Street Address 1
                        </label>
                        <v-text-field
                          v-model="SecretaryStreet1"
                          :rules="SecretaryStreet1Rules"
                          hint="Enter street address line 1"
                          required
                          outlined
                          dense
                          class="premium-input"
                          prepend-inner-icon="mdi-home-variant"
                          background-color="white"
                        ></v-text-field>
                      </div>
                    </v-col>
                    <v-col cols="12" sm="8">
                      <div class="form-group">
                        <label class="form-label">Street Address 2</label>
                        <v-text-field
                          v-model="SecretaryStreet2"
                          :rules="SecretaryStreet2Rules"
                          hint="Enter street address line 2"
                          required
                          outlined
                          dense
                          class="premium-input"
                          prepend-inner-icon="mdi-home-variant"
                          background-color="white"
                        ></v-text-field>
                      </div>
                    </v-col>
                    <v-col cols="12" sm="4">
                      <div class="form-group">
                        <label class="form-label">Area</label>
                        <v-text-field
                          v-model="SecretaryAreaName"
                          :rules="SecretaryAreaNameRules"
                          hint="Enter area name"
                          required
                          outlined
                          dense
                          class="premium-input"
                          prepend-inner-icon="mdi-map-marker"
                          background-color="white"
                        ></v-text-field>
                      </div>
                    </v-col>

                    <!-- Location Fields -->
                    <v-col cols="12" sm="6" md="4">
                      <div class="form-group">
                        <label class="form-label">
                          <span class="required-mark">*</span>State
                        </label>
                        <v-autocomplete
                          @change="getSecretaryDistrictCodeOptions"
                          :reduce="(option) => option.value"
                          :loading="SecretaryStateCodeOptionsLoading"
                          :items="SecretaryStateCodeOptions"
                          :rules="SecretaryStateCodeRules"
                          v-model="SecretaryStateCode"
                          required
                          outlined
                          dense
                          class="premium-input"
                          prepend-inner-icon="mdi-state"
                          background-color="white"
                        ></v-autocomplete>
                      </div>
                    </v-col>

                    <!-- Location Fields -->
                    <v-col cols="12" sm="6" md="4">
                      <div class="form-group">
                        <label class="form-label">
                          <span class="required-mark">*</span>District
                        </label>
                        <v-autocomplete
                          @change="getSecretaryCityCodeOptions"
                          :reduce="(option) => option.value"
                          :loading="SecretaryDistrictCodeOptionsLoading"
                          :items="SecretaryDistrictCodeOptions"
                          :rules="SecretaryDistrictCodeRules"
                          v-model="SecretaryDistrictCode"
                          required
                          outlined
                          dense
                          class="premium-input"
                          prepend-inner-icon="mdi-city-variant"
                          background-color="white"
                        ></v-autocomplete>
                      </div>
                    </v-col>
                    <v-col cols="12" sm="6" md="4">
                      <div class="form-group">
                        <label class="form-label">
                          <span class="required-mark">*</span>City
                        </label>
                        <v-autocomplete
                          :reduce="(option) => option.value"
                          :loading="SecretaryCityCodeOptionsLoading"
                          :items="SecretaryCityCodeOptions"
                          :rules="SecretaryCityCodeRules"
                          v-model="SecretaryCityCode"
                          dense
                          clearable
                          required
                          outlined
                          class="premium-input"
                          prepend-inner-icon="mdi-map-marker"
                          background-color="white"
                        ></v-autocomplete>
                      </div>
                    </v-col>

                    <!-- Contact Fields -->
                    <v-col cols="12" sm="6" md="4">
                      <div class="form-group">
                        <label class="form-label">
                          <span class="required-mark">*</span>Pincode
                        </label>
                        <v-text-field
                          v-model="SecretaryPincode"
                          :rules="SecretaryPincodeRules"
                          hint="Enter pincode"
                          required
                          outlined
                          dense
                          class="premium-input"
                          prepend-inner-icon="mdi-mail-box"
                          background-color="white"
                        ></v-text-field>
                      </div>
                    </v-col>
                    <v-col cols="12" sm="6" md="4">
                      <div class="form-group">
                        <label class="form-label">
                          <span class="required-mark">*</span>Mobile No
                        </label>
                        <v-text-field
                          v-model="SecretaryMobileNo"
                          :rules="SecretaryMobileNoRules"
                          :counter="10"
                          v-mask="'##########'"
                          type="number"
                          hint="Enter mobile number"
                          dense
                          outlined
                          class="premium-input"
                          prepend-inner-icon="mdi-phone"
                          background-color="white"
                        ></v-text-field>
                      </div>
                    </v-col>
                    <v-col cols="12" sm="6" md="4">
                      <div class="form-group">
                        <label class="form-label">
                          <span class="required-mark">*</span>Email ID
                        </label>
                        <v-text-field
                          v-model="SecretaryEmailId"
                          :rules="SecretaryEmailIdRules"
                          hint="Enter email id"
                          type="email"
                          required
                          outlined
                          dense
                          class="premium-input"
                          prepend-inner-icon="mdi-email"
                          background-color="white"
                        ></v-text-field>
                      </div>
                    </v-col>
                  </v-row>
                </v-container>
              </v-card>

              <!-- PAYMENT DETAILS CARD -->
              <v-card elevation="8" class="premium-shadow mt-6">
                <div
                  class="card-header-premium"
                  style="
                    background: linear-gradient(
                      135deg,
                      #10b981 0%,
                      #059669 100%
                    );
                  "
                >
                  <v-icon color="white" class="header-icon-large"
                    >mdi-receipt</v-icon
                  >
                  <h3 class="card-title-premium">Payment Details</h3>
                </div>
                <v-container class="card-content-wrapper">
                  <v-row wrap v-if="tableData1.length > 0">
                    <v-col align="center" cols="12" sm="12" md="12">
                      <!-- <v-text-field
                v-model="search"
                append-icon="mdi-magnify"
                label="Search records here"
                single-line
                hide-details
              ></v-text-field>-->
                      <br />
                      <v-data-table
                        class="elevation-1"
                        loading-text="Loading... Please wait"
                        disable-sort
                        disable-filtering
                        v-model="selected"
                        :search="search"
                        :loading="TableLoadingFlag"
                        :item-key="tableOptions1.ItemKey"
                        :single-select="tableOptions1.SingleSelectFlag"
                        :headers="tableColumns1"
                        :items="tableData1"
                        :items-per-page="tableOptions1.ItemsPerPage"
                        :footer-props="tableOptions1.FooterProps"
                        :hide-default-footer="tableOptions1.HideDefaultFooter"
                      >
                        <template v-slot:item.FineCheckBox="{ item }">
                          <v-checkbox
                            v-if="item.FineFlag"
                            v-model="item.FinePaidFlag"
                            :label="item.FineAmountTxt"
                            color="info"
                            :value="item.FinePaidFlag"
                            hide-details
                            v-on="calculateTotal()"
                          ></v-checkbox>
                        </template>
                        <template v-slot:item.Qty="{ item }">
                          <v-text-field
                            v-model="item.Qty"
                            v-on="calculateTotal()"
                            type="number"
                            min="0"
                            :rules="QtyRules"
                          ></v-text-field>
                        </template>
                        <template v-slot:item.SubTotal="{ item }">
                          {{ item.SubTotal }}
                        </template>
                      </v-data-table>
                    </v-col>
                  </v-row>
                  <br />
                  <v-row>
                    <v-col align="center" cols="12" sm="12" md="12">
                      <h3 class="text-primary">
                        Total Amount : Rs. {{ TotalAmount }}
                      </h3>
                    </v-col>
                  </v-row>
                </v-container>
              </v-card>

              <!-- ACTION BUTTONS -->
              <v-row wrap class="mt-8 mb-4">
                <v-col align="center" cols="12">
                  <upload-progress
                    :ProgessStart="ProgessStart"
                    :UploadPercentage="uploadPercentage"
                  ></upload-progress>
                  <v-btn
                    :disabled="!valid"
                    type="submit"
                    color="white"
                    class="modern-submit-btn mr-4"
                    @click.prevent="confirmAlert"
                    :loading="SubmitFlag"
                    elevation="8"
                    large
                  >
                    <v-icon left>mdi-credit-card</v-icon>
                    Proceed to Payment
                  </v-btn>
                </v-col>
              </v-row>
            </template>
          </v-form>
        </v-card-text>
      </v-card>
    </v-container>
  </div>
</template>

<script>
import common from "@/view/Common.vue";
import companyConfig from "@/company_config.json";
import Swal from "sweetalert2";

export default {
  mixins: [common],
  components: {},
  data() {
    return {
      valid: true,
      LoadingFlag: false,
      SubmitFlag: false,
      ProgressLoadingFlag: false,
      StartupLoadingFlag: false,

      tableData1: [],
      tableColumns1: [],
      tableOptions1: [],
      selected: [],
      search: "",
      TableLoadingFlag: false,

      TotalAmount: 0,
      Member: {},

      SelectedLom: {},

      MobileNoRules: [(v) => !!v || "Mobile No is required"],

      EmailIdRules: [
        (v) => !!v || "E-mail is required",
        (v) => /.+@.+\..+/.test(v) || "E-mail must be valid",
      ],

      QtyRules: [
        (v) => Number.isInteger(Number(v)) || "Nos is invalid",
        (v) => v >= 0 || "Number must be greater than zero",
      ],

      // NewLomCodeRules: [(v) => !!v || "New LO is required"],
      NewLomCode: "",
      NewLomCodeOptions: [],
      NewLomCodeOptionsLoading: false,

      FirstName: "",
      FirstNameRules: [(v) => !!v || "First name is required"],

      MiddleName: "",
      MiddleNameRules: [],

      LastName: "",
      LastNameRules: [],

      Street1: "",
      Street1Rules: [(v) => !!v || "Street 1 is required"],

      Street2: "",
      Street2Rules: [],

      AreaName: "",
      AreaNameRules: [],

      Pincode: "",
      PincodeRules: [(v) => !!v || "Pincode is required"],

      PresidentMobileNo: "",
      PresidentMobileNoRules: [(v) => !!v || "Mobile No is required"],

      PresidentEmailId: "",
      PresidentEmailIdRules: [
        (v) => !!v || "E-mail is required",
        (v) => /.+@.+\..+/.test(v) || "E-mail must be valid",
      ],

      StateCodeRules: [(v) => !!v || "State is required"],
      StateCode: "",
      StateCodeOptions: [],
      StateCodeOptionsLoading: false,

      DistrictCodeRules: [(v) => !!v || "District is required"],
      DistrictCode: "",
      DistrictCodeOptions: [],
      DistrictCodeOptionsLoading: false,

      CityCodeRules: [(v) => !!v || "City is required"],
      CityCode: "",
      CityCodeOptions: [],
      CityCodeOptionsLoading: false,

      // Lady LO Flag
      IsLadyLo: "",
      IsLadyLoRules: [
        (v) => !!v || "Please select whether this is a Lady LO or not",
      ],

      // Secretary Details
      SecretaryFirstName: "",
      SecretaryFirstNameRules: [(v) => !!v || "First name is required"],

      SecretaryMiddleName: "",
      SecretaryMiddleNameRules: [],

      SecretaryLastName: "",
      SecretaryLastNameRules: [],

      SecretaryStreet1: "",
      SecretaryStreet1Rules: [(v) => !!v || "Street 1 is required"],

      SecretaryStreet2: "",
      SecretaryStreet2Rules: [],

      SecretaryAreaName: "",
      SecretaryAreaNameRules: [],

      SecretaryPincode: "",
      SecretaryPincodeRules: [(v) => !!v || "Pincode is required"],

      SecretaryMobileNo: "",
      SecretaryMobileNoRules: [(v) => !!v || "Mobile No is required"],

      SecretaryEmailId: "",
      SecretaryEmailIdRules: [
        (v) => !!v || "E-mail is required",
        (v) => /.+@.+\..+/.test(v) || "E-mail must be valid",
      ],

      SecretaryStateCodeRules: [(v) => !!v || "State is required"],
      SecretaryStateCode: "",
      SecretaryStateCodeOptions: [],
      SecretaryStateCodeOptionsLoading: false,

      SecretaryDistrictCodeRules: [(v) => !!v || "District is required"],
      SecretaryDistrictCode: "",
      SecretaryDistrictCodeOptions: [],
      SecretaryDistrictCodeOptionsLoading: false,

      SecretaryCityCodeRules: [(v) => !!v || "City is required"],
      SecretaryCityCode: "",
      SecretaryCityCodeOptions: [],
      SecretaryCityCodeOptionsLoading: false,

      SelectedLoName: "",

      ProgessStart: 0,
      uploadPercentage: 0,

      CurrentYearId: 0,

      ZoneCode: "",
      LomCode: "",
      YearCode: "",
      MonthCode: "",
      MonthName: "",

      OrientationCertificate: {},
      MembershipList: {},
      PageInfo: {},
      PageTitle: "",
      PageDescription: "",
      JciYearCode: "",
      YearName: "",

      CartDetails: {},
      Order: {},

      rows: [],
    };
  },
  mounted() {},
  watch: {
    PageInfo: function () {
      console.log("watch PageInfo");
      this.LoadingFlag = false;
      var tr = this.PageInfo;
      console.log({ tr });
      var n1 = Object.keys(tr).length;
      console.log({ n1 });
      if (n1 > 0) {
        var flag = tr.PagePermissionFlag;
        console.log("PagePermissionFlag=" + flag);

        if (!flag) {
          this.$router.push("/access-denied");
        }
        this.PageTitle = tr.PageTitle;
        this.PageDescription = tr.PageDescription;
        this.JciYearCode = tr.CurrentJciYearId;
        var YearName = tr.CurrentJciYearName;
        this.YearName = YearName;

        var MonthCode = this.$route.query.month;
        MonthCode = MonthCode == (null || undefined) ? 0 : MonthCode;
        console.log({ MonthCode });
        this.MonthCode = MonthCode;

        var tempDate1 = YearName + "-" + MonthCode + "-01";
        console.log({ tempDate1 });

        var tempDate2 = new Date(tempDate1);
        console.log({ tempDate2 });
        var MonthName = tempDate2.toLocaleString("en-us", { month: "long" });
        console.log({ MonthName });
        this.MonthName = MonthName;

        var ZoneCode = this.$session.get("ZoneId");
        ZoneCode = ZoneCode == (null || undefined) ? "" : ZoneCode;
        console.log("ZoneCode=" + ZoneCode);
        this.ZoneCode = ZoneCode;

        var LomCode = this.$session.get("LomId");
        LomCode = LomCode == (null || undefined) ? "" : LomCode;
        console.log("LomCode=" + LomCode);
        this.LomCode = LomCode;

        this.pageData();
      }
    },
    CartDetails: function () {
      console.log("watch CartDetails");
      this.LoadingFlag = false;
      this.TableLoadingFlag = false;
      var tr = this.CartDetails;
      console.log({ tr });
      var n1 = Object.keys(tr).length;
      console.log({ n1 });
      if (n1 > 0) {
        var tableOptions = tr.TableOptions;
        this.tableOptions1 = tableOptions;
        this.tableColumns1 = tr.TableHeader;
        this.tableData1 = tr.TableData;
        this.TotalAmount = tableOptions.TotalAmount;
      }
    },
    NewLomCodeOptions: function () {
      console.log("watch NewLomCodeOptions");
      this.LoadingFlag = false;
      this.NewLomCodeOptionsLoading = false;
    },
    StateCodeOptions: function () {
      console.log("watch StateCodeOptions");
      this.LoadingFlag = false;
      this.StateCodeOptionsLoading = false;
    },
    DistrictCodeOptions: function () {
      console.log("watch DistrictCodeOptions");
      this.LoadingFlag = false;
      this.DistrictCodeOptionsLoading = false;
    },
    CityCodeOptions: function () {
      console.log("watch CityCodeOptions");
      this.LoadingFlag = false;
      this.CityCodeOptionsLoading = false;
    },
    SecretaryStateCodeOptions: function () {
      console.log("watch SecretaryStateCodeOptions");
      this.LoadingFlag = false;
      this.SecretaryStateCodeOptionsLoading = false;
    },
    SecretaryDistrictCodeOptions: function () {
      console.log("watch SecretaryDistrictCodeOptions");
      this.LoadingFlag = false;
      this.SecretaryDistrictCodeOptionsLoading = false;
    },
    SecretaryCityCodeOptions: function () {
      console.log("watch SecretaryCityCodeOptions");
      this.LoadingFlag = false;
      this.SecretaryCityCodeOptionsLoading = false;
    },
    LomName: function () {
      console.log("watch LomName");
      this.generateSelectedLoName();
    },
    NewLomCode: function () {
      console.log("watch MemberId");
      var temp_code = this.NewLomCode;
      console.log("temp_code=" + temp_code);
      var idx = this.NewLomCodeOptions.map((e) => e.value).indexOf(temp_code);
      console.log("idx=" + idx);
      var SelectedLom = {};
      if (idx >= 0) {
        SelectedLom = this.NewLomCodeOptions[idx];
        console.log({ SelectedLom });
      }
      this.SelectedLom = SelectedLom;
    },
  },
  methods: {
    backPage() {
      this.$router.go(-1);
    },
    formatDate(dateStr) {
      if (!dateStr) return "N/A";
      try {
        const options = { year: "numeric", month: "short", day: "numeric" };
        const date = new Date(dateStr);
        return date.toLocaleDateString("en-US", options);
      } catch (error) {
        return dateStr;
      }
    },
    refreshPageData() {
      console.log("refreshPageData called");

      this.LoadingFlag = true;
      var url1 = "api/menu-details/show";
      var condition1 = {
        UserInterface: 1,
        Module: "provisional_lo",
        Action: "create_1",
      };
      this.getPageDetails("PageInfo", {}, url1, condition1);

      var url2 = "api/new-lom/cart-details";
      var condition2 = {
        UserInterface: 1,
      };
      // this.TableLoadingFlag = true;
      // this.getPageDetails("CartDetails", {}, url2, condition2);
    },
    pageData() {
      console.log("pageData called");
      this.getNewLomCodeOptions();
      this.getStateCodeOptions();
      this.getSecretaryStateCodeOptions();
      this.getMemberDetails();
      this.getRegularSubscriptionList();
    },
    validate() {
      if (this.$refs.form2.validate()) {
        this.snackbar = true;
      }
    },
    resetForm() {
      this.$refs.form2.reset();
      this.tableData1 = [];
    },
    resetValidation() {
      this.$refs.form2.resetValidation();
    },
    getNewLomCodeOptions() {
      console.log("getNewLomCodeOptions called");
      this.LoadingFlag = true;
      this.NewLomCodeOptionsLoading = true;
      var selectbox1_source = "NewLomCode";
      var selectbox1_destination = "NewLomCodeOptions";
      var selectbox1_url = "api/new-lom/options";
      var selectbox1_conditions_array = {
        UserInterface: 2,
      };
      this.getSelectBoxOptions(
        selectbox1_source,
        selectbox1_destination,
        selectbox1_conditions_array,
        selectbox1_url
      );
    },
    getStateCodeOptions() {
      console.log("getStateCodeOptions called");
      this.LoadingFlag = true;
      this.StateCodeOptionsLoading = true;
      var selectbox1_source = "StateCode";
      var selectbox1_destination = "StateCodeOptions";
      var selectbox1_url = "api/states/options";
      var selectbox1_conditions_array = {
        UserInterface: 1,
      };
      this.getSelectBoxOptions(
        selectbox1_source,
        selectbox1_destination,
        selectbox1_conditions_array,
        selectbox1_url
      );
    },
    getDistrictCodeOptions() {
      console.log("getDistrictCodeOptions called");
      var StateCode = this.StateCode;
      console.log({ StateCode });
      if (StateCode != "") {
        this.LoadingFlag = true;
        this.DistrictCodeOptionsLoading = true;
        var selectbox1_source = "DistrictCode";
        var selectbox1_destination = "DistrictCodeOptions";
        var selectbox1_url = "api/district/options";
        var selectbox1_conditions_array = {
          UserInterface: 1,
          StatesCode: StateCode,
        };
        this.getSelectBoxOptions(
          selectbox1_source,
          selectbox1_destination,
          selectbox1_conditions_array,
          selectbox1_url
        );
      }
    },
    getCityCodeOptions() {
      console.log("getCityCodeOptions called");
      var DistrictCode = this.DistrictCode;
      console.log({ DistrictCode });
      if (DistrictCode != "") {
        this.CityCodeOptionsLoading = true;
        var selectbox1_source = "CityCode";
        var selectbox1_destination = "CityCodeOptions";
        var selectbox1_url = "api/city/options";
        var selectbox1_conditions_array = {
          UserInterface: 2,
          DistrictCode: DistrictCode,
        };
        this.getSelectBoxOptions(
          selectbox1_source,
          selectbox1_destination,
          selectbox1_conditions_array,
          selectbox1_url
        );
      }
    },
    getSecretaryStateCodeOptions() {
      console.log("getSecretaryStateCodeOptions called");
      this.LoadingFlag = true;
      this.SecretaryStateCodeOptionsLoading = true;
      var selectbox1_source = "SecretaryStateCode";
      var selectbox1_destination = "SecretaryStateCodeOptions";
      var selectbox1_url = "api/states/options";
      var selectbox1_conditions_array = {
        UserInterface: 1,
      };
      this.getSelectBoxOptions(
        selectbox1_source,
        selectbox1_destination,
        selectbox1_conditions_array,
        selectbox1_url
      );
    },
    getSecretaryDistrictCodeOptions() {
      console.log("getSecretaryDistrictCodeOptions called");
      var SecretaryStateCode = this.SecretaryStateCode;
      console.log({ SecretaryStateCode });
      if (SecretaryStateCode != "") {
        this.LoadingFlag = true;
        this.SecretaryDistrictCodeOptionsLoading = true;
        var selectbox1_source = "SecretaryDistrictCode";
        var selectbox1_destination = "SecretaryDistrictCodeOptions";
        var selectbox1_url = "api/district/options";
        var selectbox1_conditions_array = {
          UserInterface: 1,
          StatesCode: SecretaryStateCode,
        };
        this.getSelectBoxOptions(
          selectbox1_source,
          selectbox1_destination,
          selectbox1_conditions_array,
          selectbox1_url
        );
      }
    },
    getSecretaryCityCodeOptions() {
      console.log("getSecretaryCityCodeOptions called");
      var SecretaryDistrictCode = this.SecretaryDistrictCode;
      console.log({ SecretaryDistrictCode });
      if (SecretaryDistrictCode != "") {
        this.SecretaryCityCodeOptionsLoading = true;
        var selectbox1_source = "SecretaryCityCode";
        var selectbox1_destination = "SecretaryCityCodeOptions";
        var selectbox1_url = "api/city/options";
        var selectbox1_conditions_array = {
          UserInterface: 2,
          DistrictCode: SecretaryDistrictCode,
        };
        this.getSelectBoxOptions(
          selectbox1_source,
          selectbox1_destination,
          selectbox1_conditions_array,
          selectbox1_url
        );
      }
    },
    getMemberDetails() {
      console.log("getMemberDetails is called");

      var MemberId = this.$session.get("MemberId");
      MemberId = MemberId == (null || undefined) ? "" : MemberId;

      console.log("MemberId=" + MemberId);

      if (MemberId != "") {
        this.StartupLoadingFlag = true;
        this.Member = {};

        var server_url = companyConfig.apiURL;
        var token = this.$session.get("token");
        token = token == (null || undefined) ? 0 : token;
        var add_url = server_url + "api/members/show";
        var upload = {
          UserInterface: 1,
          MemberId: MemberId,
        };
        console.log("upload=" + JSON.stringify(upload));
        console.log(
          "server_url=" +
            server_url +
            ", token=" +
            token +
            ", add_url=" +
            add_url
        );

        const thisIns = this;
        var output = "";
        var records = "";
        var successTxt = "";
        var errorTxt = "";
        var flag = 0;

        this.$http({
          url: add_url,
          method: "POST",
          data: upload,
          headers: {
            "Content-Type": "application/json",
            Authorization: "Bearer " + token,
          },
          onUploadProgress: function (progressEvent) {
            this.uploadPercentage = parseInt(
              Math.round((progressEvent.loaded / progressEvent.total) * 100)
            );
          }.bind(this),
        })
          .then(function (response) {
            console.log("response=" + JSON.stringify(response));

            thisIns.StartupLoadingFlag = false;

            output = response.data.output;
            flag = response.data.flag;
            output = response.data.output;
            records = response.data.records;
            console.log("output=" + output + ", flag=" + flag);
            successTxt = response.data.success;
            errorTxt = response.data.error;
            console.log("successTxt=" + successTxt + ", errorTxt=" + errorTxt);

            if (flag == 1) {
              var member = records[0];
              thisIns.Member = member;
              thisIns.MemberImageFlag = true;
              thisIns.Member.EmailId =
                member.EmailId == "NA" ? "" : member.EmailId;
              thisIns.Member.PrimaryMobile =
                member.PrimaryMobile == "NA" ? "" : member.PrimaryMobile;
              // thisIns.toast("success", output);
              // thisIns.resetForm();
            } else {
              thisIns.toast("error", output);
            }
          })
          .catch(function (error) {
            console.log("error=" + error);
            thisIns.StartupLoadingFlag = false;
          });
      } else {
        var message = "Member ID not found. Please login again.";
        this.toast("error", message);
        this.StartupLoadingFlag = false;
      }
    },
    generateSelectedLoName() {
      console.log("generateSelectedLoName called");

      var CityCode = this.CityCode;
      var LomName = this.LomName;
      var SelectedLoName = "";

      if (CityCode != "") {
        var City = this.CityCodeOptions.find((data) => {
          return data.value == CityCode;
        });

        console.log("Selected City=" + JSON.stringify(City));

        SelectedLoName = "jci " + City.text + " " + LomName;
      } else {
        SelectedLoName = "jci {city name} " + LomName;
      }
      SelectedLoName = SelectedLoName.toUpperCase();
      console.log("SelectedLoName=" + SelectedLoName);

      this.SelectedLoName = SelectedLoName;
    },
    getRegularSubscriptionList() {
      console.log("getRegularSubscriptionList is called");

      var JciYearCode = this.JciYearCode;
      console.log("JciYearCode=" + JciYearCode);

      // JciYearCode = "";

      if (JciYearCode != "") {
        this.LoadingFlag = true;
        this.tableData1 = [];

        var server_url = companyConfig.apiURL;
        var token = this.$session.get("token");
        token = token == (null || undefined) ? 0 : token;
        var add_url = server_url + "api/year-wise-contribution/lists";
        var upload = {
          UserInterface: 3,
          YearCode: JciYearCode,
        };
        console.log("upload=" + JSON.stringify(upload));
        console.log(
          "server_url=" +
            server_url +
            ", token=" +
            token +
            ", add_url=" +
            add_url
        );

        const thisIns = this;
        var output = "";
        var records = "";
        var successTxt = "";
        var errorTxt = "";
        var flag = 0;

        this.$http({
          url: add_url,
          method: "POST",
          data: upload,
          headers: {
            "Content-Type": "application/json",
            Authorization: "Bearer " + token,
          },
          onUploadProgress: function (progressEvent) {
            this.uploadPercentage = parseInt(
              Math.round((progressEvent.loaded / progressEvent.total) * 100)
            );
          }.bind(this),
        })
          .then(function (response) {
            console.log("response=" + JSON.stringify(response));

            // thisIns.pageLoadingOff()
            thisIns.LoadingFlag = false;

            output = response.data.output;
            flag = response.data.flag;
            output = response.data.output;
            records = response.data.records;
            console.log("output=" + output + ", flag=" + flag);
            successTxt = response.data.success;
            errorTxt = response.data.error;
            console.log("successTxt=" + successTxt + ", errorTxt=" + errorTxt);

            thisIns.tableOptions1 = records.TableOptions;

            if (flag == 1) {
              thisIns.tableColumns1 = records.TableHeader;
              thisIns.tableData1 = records.TableData;
              thisIns.toast("success", output);
              // thisIns.resetForm();
            } else {
              thisIns.toast("error", output);
            }
          })
          .catch(function (error) {
            console.log("error=" + error);
            this.SearchFlag = false;
          });
      } else {
        var message = "";
        if (JciYearCode == "") {
          message += "Year should not be empty. ";
        }
        this.toast("error", message);
      }
    },
    calculateTotal() {
      console.log("calculateTotal called");
      var n1 = this.tableData1.length;
      var totalAmount = 0;
      // console.log("n1=" + n1 + ", tableData1=" + JSON.stringify(this.tableData1));
      for (var i = 0; i < n1; i++) {
        var qty = this.tableData1[i]["Qty"];
        var PenaltyAmount = this.tableData1[i]["FineAmount"];
        var WithoutFineAmount = this.tableData1[i]["WithoutFineAmount"];
        var FinePaidFlag = this.tableData1[i]["FinePaidFlag"];
        var amount = this.tableData1[i]["Amount"];
        console.log(
          "i=" +
            i +
            ", qty=" +
            qty +
            ", PenaltyAmount=" +
            PenaltyAmount +
            ", WithoutFineAmount=" +
            WithoutFineAmount +
            ", FinePaidFlag=" +
            FinePaidFlag +
            ", amount=" +
            amount
        );
        amount = FinePaidFlag ? amount : WithoutFineAmount;
        PenaltyAmount = FinePaidFlag ? PenaltyAmount : 0;
        console.log("amount=" + amount + ", PenaltyAmount=" + PenaltyAmount);
        qty = !isNaN(qty) ? (qty >= 0 ? qty : 0) : 0;
        amount = !isNaN(amount) ? amount : 0;
        console.log("qty=" + qty + ", amount=" + amount);
        var subTotal = parseFloat(qty) * parseFloat(amount);
        this.tableData1[i]["PenaltyAmount"] = PenaltyAmount;
        this.tableData1[i]["PenaltyAmountTxt"] = this.getDecimalNumber(
          PenaltyAmount,
          2
        );
        this.tableData1[i]["SubTotal"] = isNaN(subTotal)
          ? 0
          : this.getDecimalNumber(subTotal, 2);
      }
      var totalAmount = this.tableData1.reduce(function (sum, line) {
        var lineTotal = parseFloat(line.SubTotal);
        console.log("lineTotal=" + lineTotal);
        if (!isNaN(lineTotal)) {
          return sum + lineTotal;
        }
      }, 0);
      console.log("totalAmount=" + totalAmount);
      this.TotalAmount = isNaN(totalAmount)
        ? 0
        : this.getDecimalNumber(totalAmount, 2);
    },
    confirmAlert() {
      var validate1 = this.$refs.form2.validate();
      console.log({ validate1 });

      var TotalAmount = this.TotalAmount;
      console.log({ TotalAmount });

      var Member = this.Member;
      console.log({ Member });

      var MemberId = Member.MemberId;
      console.log({ MemberId });

      // TotalAmount = 0;

      if (validate1 && TotalAmount > 0 && MemberId != "") {
        var htmlTxt = "";
        // htmlTxt += "<b>Please verify all the information before proceeding</b>";
        htmlTxt += "<b>Selected New LO details</b>";
        htmlTxt += "<br/><br/>";
        htmlTxt +=
          "Your Mobile Number <b>" + this.Member.PrimaryMobile + "</b><br/>";
        htmlTxt += "Your Email Id <b>" + this.Member.EmailId + "</b><br/>";
        htmlTxt +=
          "Selected LO Name <b>" + this.SelectedLom.NewLomsName + "</b><br/>";
        htmlTxt += "New LO President name <b>" + this.FirstName + "</b><br/>";
        htmlTxt +=
          "New LO President Mobile Number <b>" +
          this.PresidentMobileNo +
          "</b><br/>";
        htmlTxt +=
          "New LO President Email Id <b>" + this.PresidentEmailId + "</b><br/>";

        htmlTxt += "<br/><b>New Secretary Details</b><br/>";
        htmlTxt +=
          "New Secretary name <b>" + this.SecretaryFirstName + "</b><br/>";
        htmlTxt +=
          "New Secretary Mobile Number <b>" +
          this.SecretaryMobileNo +
          "</b><br/>";
        htmlTxt +=
          "New Secretary Email Id <b>" + this.SecretaryEmailId + "</b><br/>";

        htmlTxt += "Amount. <b>" + TotalAmount + "</b><br/>";
        htmlTxt += "<br/><br/>";
        htmlTxt += "Do you want to continue?";
        Swal.fire({
          title: "Please verify all the information before proceeding",
          text: "",
          icon: "warning",
          html: htmlTxt,
          showCancelButton: true,
          confirmButtonText: `Continue`,
          confirmButtonColor: "#3085d6",
          cancelButtonText: `Cancel`,
          cancelButtonColor: "#d33",
        }).then((result) => {
          /* Read more about isConfirmed, isDenied below */
          if (result.isConfirmed) {
            this.submitForm();
          } else {
            this.sweetAlert("error", "You cancelled the process", true);
          }
        });
      } else {
        var message = "";
        if (!validate1) {
          message += "Kindly fill the required fields. ";
        }
        if (TotalAmount == 0) {
          message += "Payment details invalid. ";
        }
        if (MemberId == 0) {
          message += "President details invaid. ";
        }
        this.sweetAlert("error", message, false);
      }
    },
    submitForm() {
      console.log("submitForm is called");

      this.ProgressLoadingFlag = true;
      this.SubmitFlag = true;

      var Member = this.Member;
      console.log({ Member });

      var MemberId = Member.MemberId;
      console.log({ MemberId });

      var SelectedLom = this.SelectedLom;
      console.log({ SelectedLom });

      var server_url = companyConfig.apiURL;
      var token = this.$session.get("token");
      token = token == (null || undefined) ? 0 : token;
      var add_url = server_url + "api/new-lom-payment/order-create";
      var uploadData = {
        Lom: this.LomCode,
        Member: MemberId,
        MobileNo: Member.PrimaryMobile,
        EmailId: Member.EmailId,
        NewLomName: this.NewLomCode,
        LomState: SelectedLom.StateId,
        FirstName: this.FirstName,
        MiddleName: this.MiddleName,
        LastName: this.LastName,
        NewPresidentMobileNo: this.PresidentMobileNo,
        NewPresidentEmailId: this.PresidentEmailId,
        Street1: this.Street1,
        Street2: this.Street2,
        AreaName: this.AreaName,
        Pincode: this.Pincode,
        States: this.StatesCode,
        District: this.DistrictCode,
        City: this.CityCode,
        IsLadyLo: this.IsLadyLo ? 1 : 0,

        SecretaryFirstName: this.SecretaryFirstName,
        SecretaryMiddleName: this.SecretaryMiddleName,
        SecretaryLastName: this.SecretaryLastName,
        SecretaryMobileNo: this.SecretaryMobileNo,
        SecretaryEmailId: this.SecretaryEmailId,
        SecretaryStreet1: this.SecretaryStreet1,
        SecretaryStreet2: this.SecretaryStreet2,
        SecretaryAreaName: this.SecretaryAreaName,
        SecretaryPincode: this.SecretaryPincode,
        SecretaryState: this.SecretaryStateCode,
        SecretaryDistrict: this.SecretaryDistrictCode,
        SecretaryCity: this.SecretaryCityCode,

        Contribution: this.tableData1,
        TotalAmount: this.TotalAmount,
      };

      let upload = new FormData();
      upload.append("Form", JSON.stringify(uploadData));
      upload.append("UserInterface", 1);
      upload.append("OrientationCertificate", this.OrientationCertificate);
      upload.append("MembershipList", this.MembershipList);

      console.log("upload=" + JSON.stringify(upload));
      console.log(
        "server_url=" + server_url + ", token=" + token + ", add_url=" + add_url
      );

      const thisIns = this;
      var output = "";
      var records = {};
      var flag = 0;

      this.$http({
        url: add_url,
        method: "POST",
        data: upload,
        headers: {
          "Content-Type": "application/json",
          Authorization: "Bearer " + token,
        },
        onUploadProgress: function (progressEvent) {
          this.uploadPercentage = parseInt(
            Math.round((progressEvent.loaded / progressEvent.total) * 100)
          );
        }.bind(this),
      })
        .then(function (response) {
          console.log("response=" + JSON.stringify(response));

          thisIns.ProgressLoadingFlag = false;
          thisIns.SubmitFlag = false;

          flag = response.data.flag;
          console.log({ flag });

          output = response.data.output;
          console.log({ output });

          records = response.data.records;
          console.log({ records });

          if (flag == 1) {
            thisIns.toast("success", output);
            thisIns.Order = records.Order;
            thisIns.makePayment();
          } else {
            thisIns.sweetAlert("error", output, false);
          }
        })
        .catch(function (error) {
          console.log("error=" + error);
          thisIns.SubmitFlag = false;
          thisIns.ProgressLoadingFlag = false;
          console.log({ error });
        });
    },
    makePayment() {
      console.log("makePayment");
      // ev.preventDefault();
      var order = this.Order;
      const thisIns = this;
      var rzp1 = new Razorpay({
        key: order.rzp_key,
        account_id: order.rzp_account_id,
        order_id: order.rzp_order_id,
        amount: order.rzp_amount,
        name: order.rzp_name,
        currency: "INR",
        description: order.rzp_description,
        image: order.rzp_image,
        theme: {
          color: order.rzp_color,
        },
        prefill: {
          name: order.customer_name,
          email: order.customer_email_id,
          contact: order.customer_mobile_no,
        },
        notes: {
          address: "",
        },
        // callback_url: "http://domain.com/#/about/" + this.order_id,
        redirect: false,
        handler: function (response) {
          console.log("razorpay_payment_id=" + response.razorpay_payment_id);
          thisIns.ProgressLoadingFlag = true;
          thisIns.SubmitFlag = true;
          // thisIns.$store.dispatch(ADD_BODY_CLASSNAME, "page-loading");
          thisIns.pageRedirect({}, "/order/success");
        },
      });
      rzp1.open();
    },
  },
  beforeMount() {
    this.refreshPageData();
  },
};
</script>
<style lang="scss" scoped>
// Premium Color Palette
$primary-navy: #001a4d;
$secondary-navy: #003d7a;
$tertiary-cyan: #0099cc;
$success-color: #10b981;
$gradient-main: linear-gradient(
  135deg,
  $secondary-navy 0%,
  $tertiary-cyan 100%
);

// Animations
@keyframes slideDown {
  from {
    opacity: 0;
    transform: translateY(-20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

@keyframes fadeInUp {
  from {
    opacity: 0;
    transform: translateY(20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

@keyframes slideInLeft {
  from {
    opacity: 0;
    transform: translateX(-30px);
  }
  to {
    opacity: 1;
    transform: translateX(0);
  }
}

@keyframes fadeIn {
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
}

@keyframes float {
  0%,
  100% {
    transform: translateY(0px);
  }
  50% {
    transform: translateY(-10px);
  }
}

@keyframes rotate {
  from {
    transform: rotate(0deg);
  }
  to {
    transform: rotate(360deg);
  }
}

@keyframes pulse {
  0%,
  100% {
    opacity: 1;
  }
  50% {
    opacity: 0.8;
  }
}

#subscription-payment-page {
  background: linear-gradient(135deg, #f5f7fa 0%, #e8ecf1 100%);
  min-height: 100vh;
  padding: 24px 0;
  animation: fadeIn 0.6s ease-in-out;

  // PREMIUM HEADER
  .premium-header-card {
    background: $gradient-main !important;
    border-radius: 16px;
    overflow: hidden;
    animation: slideDown 0.8s cubic-bezier(0.34, 1.56, 0.64, 1);
    position: relative;
    border: none;
    box-shadow: 0 20px 60px rgba(0, 26, 77, 0.3);
    margin: 24px;

    .header-gradient-bg {
      position: absolute;
      top: 0;
      left: 0;
      right: 0;
      bottom: 0;
      background: $gradient-main;
    }

    .header-backdrop-effect {
      position: absolute;
      top: -50%;
      right: -50%;
      width: 200%;
      height: 200%;
      background: radial-gradient(
        circle,
        rgba(255, 255, 255, 0.1) 1px,
        transparent 1px
      );
      background-size: 50px 50px;
      animation: rotate 20s linear infinite;
    }
  }

  .header-content-premium {
    position: relative;
    z-index: 1;
  }

  .header-icon-box {
    background: rgba(255, 255, 255, 0.2);
    backdrop-filter: blur(10px);
    border-radius: 50%;
    width: 70px;
    height: 70px;
    display: flex;
    align-items: center;
    justify-content: center;
    box-shadow: 0 8px 32px rgba(0, 0, 0, 0.2);
    animation: float 3s ease-in-out infinite;
  }

  .header-title {
    font-size: 28px;
    font-weight: 800;
    letter-spacing: 0.5px;
    margin: 0;
    text-shadow: 2px 2px 4px rgba(0, 0, 0, 0.2);
  }

  .header-subtitle {
    font-size: 14px;
    opacity: 0.9;
    margin: 4px 0 0 0;
  }

  .premium-action-btn {
    transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1) !important;
    border: 2px solid rgba(255, 255, 255, 0.3) !important;
    border-radius: 8px !important;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.2) !important;

    &:hover {
      transform: translateY(-2px);
      background: rgba(255, 255, 255, 0.3) !important;
      box-shadow: 0 8px 20px rgba(0, 0, 0, 0.3) !important;
    }
  }

  // MAIN CARD
  .main-card {
    border-radius: 14px !important;
    box-shadow: 0 12px 40px rgba(0, 26, 77, 0.12) !important;
    animation: fadeInUp 0.6s ease-out 0.2s both;
    margin: 0 24px 24px 24px;
  }

  // CONTENT SECTION
  .content-section {
    padding: 32px !important;
    background: linear-gradient(135deg, #fafbfc 0%, #ffffff 100%);
  }

  // CARD HEADERS
  .card-header-premium {
    display: flex;
    align-items: center;
    gap: 16px;
    background: $gradient-main;
    padding: 20px 28px;
    color: white;
    animation: slideDown 0.5s ease-out;

    .header-icon-large {
      font-size: 32px !important;
      flex-shrink: 0;
    }

    .card-title-premium {
      margin: 0;
      font-size: 20px;
      font-weight: 700;
      letter-spacing: 0.3px;
      text-transform: uppercase;
    }
  }

  // CARD CONTENT WRAPPER
  .card-content-wrapper {
    padding: 28px !important;
    background: linear-gradient(135deg, #fafbfc 0%, #ffffff 100%);
  }

  // PREMIUM CARDS
  .sponsor-card,
  .newlo-card,
  .president-card {
    border-radius: 12px !important;
    box-shadow: 0 4px 16px rgba(0, 0, 0, 0.08) !important;
    border: 1px solid #e3f2fd !important;
    animation: slideInLeft 0.5s ease-out both;
    transition: all 0.3s ease;
    background: white !important;
    overflow: hidden;

    &:hover {
      box-shadow: 0 12px 32px rgba(25, 118, 210, 0.15) !important;
      transform: translateY(-4px);
    }
  }

  .newlo-card {
    animation-delay: 0.1s;
  }

  .president-card {
    animation-delay: 0.2s;
  }

  // PROFILE CARD
  .profile-card {
    text-align: center;
    animation: fadeInUp 0.5s ease-out both;
  }

  .profile-image-wrapper {
    margin-bottom: 16px;
    border-radius: 12px;
    overflow: hidden;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  }

  .profile-image {
    border-radius: 12px !important;
    transition: all 0.3s ease;

    &:hover {
      transform: scale(1.05);
    }
  }

  .profile-info {
    padding: 12px;
  }

  .profile-name {
    font-size: 16px;
    font-weight: 700;
    color: #1a237e;
    margin-bottom: 4px;
  }

  .profile-id {
    font-size: 12px;
    color: #999;
    font-weight: 600;
  }

  // INFO CARD
  .info-card {
    padding: 16px;
    background: linear-gradient(135deg, #f5f7fa 0%, #ffffff 100%);
    border-radius: 8px;
    border: 1px solid #e3f2fd;
    animation: fadeInUp 0.4s ease-out both;
    transition: all 0.3s ease;

    &:hover {
      background: linear-gradient(135deg, #e3f2fd 0%, #ffffff 100%);
      border-color: #1976d2;
      box-shadow: 0 2px 8px rgba(25, 118, 210, 0.1);
    }
  }

  .info-header {
    display: flex;
    align-items: center;
    gap: 8px;
    margin-bottom: 12px;

    .info-icon {
      font-size: 18px !important;
    }

    .info-label {
      font-size: 12px;
      font-weight: 700;
      text-transform: uppercase;
      letter-spacing: 0.3px;
      color: #666;
    }
  }

  .info-content {
    text-align: left;
  }

  .info-value {
    font-size: 14px;
    font-weight: 700;
    color: #1a237e;
    margin: 4px 0;
  }

  .info-subtext {
    font-size: 12px;
    color: #999;
    margin: 0;
  }

  // FORM STYLING
  .form-group {
    margin-bottom: 20px;
    animation: fadeInUp 0.4s ease-out both;
  }

  .form-label {
    display: block;
    font-size: 13px;
    font-weight: 700;
    color: #1a237e;
    margin-bottom: 8px;
    letter-spacing: 0.3px;
    text-transform: uppercase;

    .required-mark {
      color: #e53935;
      margin-right: 4px;
      font-size: 16px;
    }
  }

  .premium-input {
    ::v-deep .v-input__control {
      .v-input__slot {
        border-radius: 8px !important;
        background-color: white !important;
        transition: all 0.3s ease !important;

        fieldset {
          border-color: #e3f2fd !important;
          border-width: 1px !important;
        }
      }

      &:hover {
        .v-input__slot fieldset {
          border-color: #1976d2 !important;
          border-width: 2px !important;
        }
      }
    }
  }

  .file-upload-wrapper {
    position: relative;
    animation: slideInLeft 0.5s ease-out;
  }

  .file-input-premium {
    ::v-deep .v-input__control {
      .v-input__slot {
        border-radius: 8px !important;
        background-color: white !important;
        transition: all 0.3s ease !important;

        fieldset {
          border-color: #e3f2fd !important;
          border-width: 1px !important;
        }
      }

      &:hover {
        .v-input__slot fieldset {
          border-color: #10b981 !important;
          border-width: 2px !important;
        }
      }
    }
  }

  .delete-btn {
    cursor: pointer;
    transition: all 0.3s ease;

    &:hover {
      transform: scale(1.2);
    }
  }

  // SUBMIT BUTTON
  .modern-submit-btn {
    border-radius: 10px !important;
    font-weight: 800 !important;
    padding: 14px 48px !important;
    text-transform: uppercase;
    letter-spacing: 0.5px;
    background: linear-gradient(
      135deg,
      $success-color 0%,
      #059669 100%
    ) !important;
    transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1) !important;
    box-shadow: 0 8px 20px rgba(16, 185, 129, 0.3) !important;
    color: white !important;
    font-size: 14px;

    .v-icon {
      margin-right: 8px;
    }

    &:hover:not(:disabled) {
      transform: translateY(-4px);
      box-shadow: 0 12px 32px rgba(16, 185, 129, 0.4) !important;
    }

    &:active:not(:disabled) {
      transform: translateY(-2px);
    }

    &:disabled {
      opacity: 0.6;
    }
  }

  // UTILITY CLASSES
  .premium-shadow {
    box-shadow: 0 8px 24px rgba(0, 26, 77, 0.1) !important;
    border: none !important;
    overflow: hidden;
  }

  .mt-8 {
    margin-top: 32px !important;
  }

  .mb-4 {
    margin-bottom: 16px !important;
  }

  .mb-6 {
    margin-bottom: 24px !important;
  }

  .mr-4 {
    margin-right: 16px !important;
  }

  .ml-6 {
    margin-left: 24px !important;
  }

  .pa-8 {
    padding: 32px !important;
  }

  .text-right {
    text-align: right;
  }

  .align-center {
    align-items: center;
  }

  .fill-height {
    height: 100%;
  }

  .d-flex {
    display: flex;
  }

  .overflow-hidden {
    overflow: hidden;
  }

  .white--text {
    color: white;
  }

  .v-progress-circular {
    animation: pulse 1s infinite;
  }

  // SPONSOR CARD PREMIUM
  .sponsor-card {
    border-radius: 12px !important;
    box-shadow: 0 4px 16px rgba(0, 0, 0, 0.08) !important;
    border: 1px solid #e3f2fd;
    animation: slideInLeft 0.5s ease-out 0.2s both;
    transition: all 0.3s ease;
    background: white !important;
    overflow: hidden;

    &:hover {
      box-shadow: 0 8px 24px rgba(25, 118, 210, 0.12) !important;
      transform: translateY(-2px);
    }
  }

  .card-header-premium {
    display: flex;
    align-items: center;
    gap: 16px;
    background: $gradient-main;
    padding: 20px 28px;
    color: white;
    animation: slideDown 0.5s ease-out;

    .header-icon-large {
      font-size: 32px !important;
      flex-shrink: 0;
    }

    .card-title-premium {
      margin: 0;
      font-size: 20px;
      font-weight: 700;
      letter-spacing: 0.3px;
      text-transform: uppercase;
    }
  }

  .card-content-wrapper {
    padding: 28px !important;
    background: linear-gradient(135deg, #fafbfc 0%, #ffffff 100%);
  }

  .profile-card {
    display: flex;
    flex-direction: column;
    align-items: center;
    padding: 16px;
    background: linear-gradient(
      135deg,
      rgba(25, 118, 210, 0.04) 0%,
      rgba(119, 235, 255, 0.02) 100%
    );
    border-radius: 12px;
    animation: fadeInUp 0.6s ease-out both;
    transition: all 0.3s ease;

    &:hover {
      box-shadow: 0 4px 12px rgba(25, 118, 210, 0.1);
      transform: translateY(-2px);
    }
  }

  .profile-image-wrapper {
    width: 100%;
    max-width: 160px;
    margin-bottom: 12px;
    border-radius: 12px;
    overflow: hidden;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
    border: 3px solid #e3f2fd;
  }

  .profile-image {
    width: 100%;
    height: auto;
    object-fit: cover;
    animation: fadeIn 0.6s ease-out 0.4s both;
    transition: all 0.3s ease;

    &:hover {
      transform: scale(1.05);
    }
  }

  .profile-info {
    text-align: center;
    width: 100%;
  }

  .profile-name {
    font-size: 16px;
    font-weight: 800;
    color: #1a237e;
    margin: 8px 0 0 0;
    line-height: 1.4;
  }

  .profile-id {
    font-size: 12px;
    color: #999;
    font-weight: 600;
    margin: 4px 0 0 0;
  }

  .info-card {
    padding: 16px;
    background: white;
    border-radius: 10px;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
    animation: fadeInUp 0.5s ease-out both;
    transition: all 0.3s ease;
    border: 1px solid #f0f0f0;

    &:hover {
      box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
      transform: translateY(-2px);
      border-color: #e3f2fd;
    }
  }

  .info-header {
    display: flex;
    align-items: center;
    gap: 8px;
    margin-bottom: 12px;
    padding-bottom: 8px;
    border-bottom: 2px solid #f0f0f0;

    .info-icon {
      font-size: 20px !important;
    }

    .info-label {
      font-size: 12px;
      font-weight: 700;
      text-transform: uppercase;
      letter-spacing: 0.3px;
      color: #666;
    }
  }

  .info-content {
    animation: slideInLeft 0.4s ease-out both;
  }

  .info-value {
    font-size: 15px;
    font-weight: 700;
    color: #1a237e;
    margin: 0;
    word-break: break-word;
    line-height: 1.4;
  }

  .info-subtext {
    font-size: 12px;
    color: #999;
    font-weight: 600;
    margin: 4px 0 0 0;
  }

  .info-row-item {
    display: flex;
    align-items: center;
    margin-bottom: 10px;
    font-size: 12px;
    line-height: 1.6;
    animation: fadeInUp 0.4s ease-out both;
    gap: 8px;

    &:last-child {
      margin-bottom: 0;
    }
  }

  .info-label-small {
    font-weight: 700;
    color: #666;
    min-width: 70px;
    display: inline-block;
    font-size: 11px;
    text-transform: uppercase;
  }

  .info-value-small {
    color: #1a237e;
    flex: 1;
    font-weight: 600;
    word-break: break-word;
    font-size: 12px;
  }

  // MEMBER CARD
  .member-card {
    border-radius: 12px !important;
    box-shadow: 0 4px 16px rgba(0, 0, 0, 0.08) !important;
    border: 1px solid #e3f2fd;
    animation: slideInLeft 0.5s ease-out 0.2s both;
    transition: all 0.3s ease;
    background: white !important;

    &:hover {
      box-shadow: 0 8px 24px rgba(25, 118, 210, 0.12) !important;
      transform: translateY(-2px);
    }
  }

  .premium-alert-card {
    position: relative;
    overflow: hidden;
    transition: all 0.3s ease;

    &::before {
      content: "";
      position: absolute;
      top: 0;
      left: 0;
      right: 0;
      bottom: 0;
      background: linear-gradient(
        135deg,
        rgba(252, 211, 77, 0.05) 0%,
        rgba(249, 115, 22, 0.05) 100%
      );
      pointer-events: none;
    }

    &:hover {
      elevation: 12;
      transform: translateY(-2px);
    }
  }

  .premium-alert-icon {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 56px;
    height: 56px;
    background: linear-gradient(135deg, #fef3c7 0%, #fde68a 100%);
    border-radius: 12px;
    box-shadow: 0 4px 15px rgba(245, 158, 11, 0.2);
  }

  .premium-alert-content {
    position: relative;
    z-index: 1;
  }

  .premium-alert-title {
    font-family: "Segoe UI", Tahoma, Geneva, Verdana, sans-serif;
    letter-spacing: 0.5px;
  }

  .premium-highlight {
    color: #d97706;
    font-weight: 600;
    background: linear-gradient(120deg, #fef3c7, #fde68a);
    padding: 2px 6px;
    border-radius: 4px;
    display: inline-block;
  }

  @keyframes pulse-icon {
    0% {
      transform: scale(1);
    }
    50% {
      transform: scale(1.08);
    }
    100% {
      transform: scale(1);
    }
  }
}
</style>